import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthRoutes } from '@core/constants/routes.const';
import { AppInitService } from '@core/services/app-init.service';
import { AppState } from '@core/store';
import { selectPersonalInfo } from '@core/store/checkout';
import { PersonalInfo } from '@core/store/checkout/checkout-state-models';
import { selectCurrentConsultant } from '@core/store/consultant';
import { Consultant } from '@core/store/consultant/consultant-state-models';
import { selectUserLoading } from '@core/store/user';
import {
  sigunpPhoneUser,
  sigunpPhoneUserFailure,
  sigunpPhoneUserSuccess,
} from '@core/store/user/user.actions';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isUsaEnv } from '@shared/utils/environment-utils';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Subject, Subscription } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'app-create-user-account',
  templateUrl: './create-user-account.component.html',
  styleUrls: ['./create-user-account.component.scss'],
})
export class CreateUserAccountComponent implements OnInit {
  shareContactInfo: boolean = false;
  isCreatingAccount: boolean = false;
  personalInfo$: Observable<PersonalInfo>;
  loading$: Observable<boolean>;
  errorMessage: string = '';
  successMesage: string;

  readonly LoginViaPhone = AuthRoutes.VerifyPhone;

  private destroy$ = new Subject<void>();
  private subscriptions: Subscription = new Subscription();
  private isUsaEnv = isUsaEnv;

  constructor(
    private store$: Store<AppState>,
    private router: Router,
    private actions$: Actions,
    private appInitService: AppInitService,
    private reCaptchaService: ReCaptchaV3Service,
  ) {}

  ngOnInit(): void {
    this.loading$ = this.store$.select(selectUserLoading);
    this.store$.select(selectPersonalInfo).subscribe((personalInfo) => {
      let contactType = personalInfo.billingCustomer.contactMethod;
      if (contactType == 'phone') {
        this.successMesage = $localize`phone`;
      } else {
        this.successMesage = $localize`email`;
      }
    });
  }

  createAccount() {
    this.personalInfo$ = this.store$.select(selectPersonalInfo);
    const currentConsultant$: Observable<Consultant> = this.store$.select(selectCurrentConsultant);
    this.subscriptions.add(
      this.reCaptchaService
        .execute('importantAction')
        .pipe(
          withLatestFrom(this.personalInfo$, currentConsultant$),
          filter(([token]) => !!token || !this.appInitService.Settings.ec.isRecaptchaEnabled),
          filter(([, personalInfo]) => !!personalInfo),
          take(1),
        )
        .subscribe(([token, personalInfo, currentConsultant]) => {
          const phoneNumber = this.isUsaEnv
            ? '+1' + personalInfo.billingCustomer.phoneNumber
            : '+52' + personalInfo.billingCustomer.phoneNumber;
          const email =
            personalInfo.billingCustomer.email === ''
              ? phoneNumber + '@noemail.com'
              : personalInfo.billingCustomer.email;

          const userToRegister = {
            firstName: personalInfo.billingCustomer.firstName,
            lastName: personalInfo.billingCustomer.lastName,
            email: email,
            phoneNumber: phoneNumber,
            token: token,
            azureB2CId: '',
            activeConsultantProwessId: currentConsultant?.beeNumber,
            newsletters: personalInfo.billingCustomer.newsletters,
            shareWithConsultant: personalInfo.billingCustomer.updates,
            contactMethod: personalInfo.billingCustomer.contactMethod,
            password: '',
          };
          this.store$.dispatch(sigunpPhoneUser({ payload: userToRegister, token }));
        }),
    );

    this.actions$
      .pipe(ofType(sigunpPhoneUserSuccess), take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.router.navigateByUrl(this.LoginViaPhone);
      });

    this.actions$
      .pipe(ofType(sigunpPhoneUserFailure), take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.errorMessage = $localize`Account creation failed. Please contact IT`;
      });
  }
}
